import {
  ArrowTopRightOnSquareIcon,
  PencilSquareIcon,
} from "@heroicons/react/20/solid"
import { Link, useNavigate } from "react-router-dom"

import type { SolarProposalDesignDto } from "~/api/dto/solar-proposal-design.dto"
import useSolarProposalAurora from "~/api/hooks/useSolarProposalAurora"
import { buildAuroraCadUrl } from "~/utils/aurora-design"

import {
  solarContractPath,
  solarDesignerPath,
  solarProposalPath,
} from "./solar-setup-paths"
import { RESERVE_NOW_LABEL } from "../BatteryOnlyProposal/contract/contract-action"

const editButtonClass = `inline-flex items-center gap-2 rounded-full border border-bp-border bg-bp-glass-hi px-4 py-2 text-[length:var(--step--1)] font-semibold text-bp-text backdrop-blur hover:bg-bp-elevated`

/** A Vision design opens the V2 designer. */
const EditDesignButton = ({
  proposalId,
}: {
  proposalId: string
}): JSX.Element => {
  const navigate = useNavigate()
  return (
    <button
      type="button"
      className={editButtonClass}
      onClick={() => navigate(solarDesignerPath(proposalId))}
    >
      <PencilSquareIcon aria-hidden="true" className="size-4" />
      Edit design
    </button>
  )
}

/** An Aurora design is edited in Aurora's CAD, in a new tab. */
const EditInAuroraLink = ({
  proposalId,
}: {
  proposalId: string
}): JSX.Element | null => {
  const aurora = useSolarProposalAurora(proposalId)
  const design = aurora.data?.activeDesign
  const cadUrl = design
    ? buildAuroraCadUrl(design.auroraProjectId, design.id)
    : null
  if (!cadUrl) return null

  return (
    <a
      href={cadUrl}
      target="_blank"
      rel="noreferrer"
      className={editButtonClass}
    >
      <ArrowTopRightOnSquareIcon aria-hidden="true" className="size-4" />
      Edit in Aurora
    </a>
  )
}

/** The design's edit entry point, by where the design lives. */
export const SolarEditDesignAction = ({
  proposalId,
  design,
}: {
  proposalId: string
  design: SolarProposalDesignDto
}): JSX.Element =>
  design.source === `aurora` ? (
    <EditInAuroraLink proposalId={proposalId} />
  ) : (
    <EditDesignButton proposalId={proposalId} />
  )

/**
 * Reserve now beneath the investment card, as Battery's Financial Summary
 * offers it: the Contract drawer, which sends the contract or routes a
 * partner-run plan (Concert, LightReach) to its Financing Status.
 */
export const SolarReserveNowLink = ({
  proposalId,
}: {
  proposalId: string
}): JSX.Element => (
  <div className="mt-7 print:hidden">
    <Link
      to={solarContractPath(proposalId)}
      state={{ returnTo: solarProposalPath(proposalId) }}
      data-testid="solar-financial-summary-reserve-now"
      className="font-display inline-flex items-center gap-2 rounded-full bg-white px-8 py-4 text-[length:var(--step-0)] font-bold text-bp-accent shadow-[0_12px_32px_rgba(0,0,0,0.25)] hover:bg-white/90 focus-visible:ring-2 focus-visible:ring-white focus-visible:outline-none"
    >
      {RESERVE_NOW_LABEL}
      <span aria-hidden="true">→</span>
    </Link>
  </div>
)
